/**
 * Portal の API クライアント。
 * - Studio と同じ JWT を localStorage `pix-studio:token` で共有
 * - VITE_API_URL で API ホストを上書き可
 */

import type {
  Author,
  GenreKey,
  LibraryEntry,
  LibraryStatus,
  MonthlySales,
  NotificationItem,
  RankEntry,
  Review,
  TagDef,
  Work,
} from "./data";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.pixadv.net";

/** R2 上の相対パス(thumbnail / screenshot)を絶対 URL に解決 */
export function workImageUrl(workId: string, path: string | undefined): string | null {
  if (!path) return null;
  return `${API_BASE}/api/portal/play/${encodeURIComponent(workId)}/assets/${encodeURI(path)}`;
}

const TOKEN_KEY = "pix-studio:token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

interface FetchOpts {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.auth) {
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? (parsed as { error: string }).error
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }
  return parsed as T;
}

export class ApiError extends Error {
  status: number;
  constructor(msg: string, status: number) {
    super(msg);
    this.status = status;
  }
}

// =============================================================
// Auth
// =============================================================

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  createdAt: number;
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await apiFetch<{ token: string; user: AuthUser }>("/api/auth/login", {
    method: "POST",
    body: { email, password },
  });
  setToken(res.token);
  return res.user;
}

export async function signup(
  email: string,
  password: string,
  displayName: string,
): Promise<AuthUser> {
  const res = await apiFetch<{ token: string; user: AuthUser }>("/api/auth/signup", {
    method: "POST",
    body: { email, password, displayName },
  });
  setToken(res.token);
  return res.user;
}

export async function demoLogin(): Promise<AuthUser> {
  const res = await apiFetch<{ token: string; user: AuthUser }>(
    "/api/portal/auth/demo",
    { method: "POST" },
  );
  setToken(res.token);
  return res.user;
}

/** Google ID トークン(GIS の credential)を渡してログイン */
export async function googleLogin(credential: string): Promise<AuthUser> {
  const res = await apiFetch<{ token: string; user: AuthUser }>(
    "/api/auth/google",
    { method: "POST", body: { credential } },
  );
  setToken(res.token);
  return res.user;
}

/** Google ログインの設定取得(clientId が無ければボタンを出さない) */
export async function fetchGoogleConfig(): Promise<{
  available: boolean;
  clientId: string | null;
}> {
  return apiFetch("/api/auth/google/config");
}

export async function fetchMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>("/api/auth/me", { auth: true });
}

export function logout(): void {
  setToken(null);
}

// =============================================================
// Portal endpoints
// =============================================================

export interface ListWorksParams {
  q?: string;
  genres?: GenreKey[];
  tags?: string[];
  prices?: string[];
  ages?: string[];
  sort?: string;
  limit?: number;
  offset?: number;
}

export async function listWorks(params: ListWorksParams = {}): Promise<Work[]> {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.genres?.length) sp.set("genres", params.genres.join(","));
  if (params.tags?.length) sp.set("tags", params.tags.join(","));
  if (params.prices?.length) sp.set("prices", params.prices.join(","));
  if (params.ages?.length) sp.set("ages", params.ages.join(","));
  if (params.sort) sp.set("sort", params.sort);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));
  const q = sp.toString();
  const res = await apiFetch<{ works: ApiWork[] }>(`/api/portal/works${q ? `?${q}` : ""}`);
  return res.works.map(normalizeWork);
}

/** API から返る Work(日時が ms): フロント側の表記文字列に正規化 */
interface ApiWork extends Omit<Work, "updatedAt" | "publishedAt"> {
  updatedAt: number;
  publishedAt?: number | null;
}

function normalizeWork(w: ApiWork): Work {
  return {
    ...w,
    updatedAt: formatDate(w.updatedAt),
    publishedAt: w.publishedAt ? formatDate(w.publishedAt) : undefined,
  };
}

export async function fetchPickup(): Promise<Work | null> {
  const res = await apiFetch<{ work: ApiWork | null }>("/api/portal/works/pickup");
  return res.work ? normalizeWork(res.work) : null;
}

export async function fetchRankingWithWorks(): Promise<
  Array<{ work: Work; rank: number; prev: number | null }>
> {
  const res = await apiFetch<{
    ranking: Array<{ work: ApiWork; rank: number; prev: number | null }>;
  }>("/api/portal/works/ranking");
  return res.ranking.map((r) => ({
    work: normalizeWork(r.work),
    rank: r.rank,
    prev: r.prev,
  }));
}

export async function fetchNewWorks(): Promise<Work[]> {
  const res = await apiFetch<{ works: ApiWork[] }>("/api/portal/works/new");
  return res.works.map(normalizeWork);
}

export async function fetchWork(id: string): Promise<Work> {
  const res = await apiFetch<{ work: ApiWork }>(
    `/api/portal/works/${encodeURIComponent(id)}`,
  );
  return normalizeWork(res.work);
}

export async function fetchSimilarWorks(id: string): Promise<Work[]> {
  const res = await apiFetch<{ works: ApiWork[] }>(
    `/api/portal/works/${encodeURIComponent(id)}/similar`,
  );
  return res.works.map(normalizeWork);
}

export async function fetchReviews(
  workId: string,
): Promise<{ reviews: Review[]; tones: Array<{ name: string; count: number }> }> {
  return apiFetch<{
    reviews: Review[];
    tones: Array<{ name: string; count: number }>;
  }>(`/api/portal/works/${encodeURIComponent(workId)}/reviews`);
}

export async function fetchAuthor(id: string): Promise<Author & { workCount: number }> {
  const res = await apiFetch<{ author: Author & { workCount: number } }>(
    `/api/portal/users/${encodeURIComponent(id)}`,
  );
  return res.author;
}

export async function fetchAuthorWorks(id: string): Promise<Work[]> {
  const res = await apiFetch<{ works: ApiWork[] }>(
    `/api/portal/users/${encodeURIComponent(id)}/works`,
  );
  return res.works.map(normalizeWork);
}

export async function fetchTags(): Promise<TagDef[]> {
  const res = await apiFetch<{ tags: TagDef[] }>("/api/portal/tags");
  return res.tags;
}

export interface LibraryEntryWithWork extends LibraryEntry {
  work: Work | null;
}

export async function fetchMyLibrary(): Promise<LibraryEntryWithWork[]> {
  const res = await apiFetch<{
    entries: Array<{
      workId: string;
      purchasedAt: number;
      progress: number;
      status: LibraryStatus;
      isOwn?: boolean;
      work: ApiWork | null;
    }>;
  }>("/api/portal/me/library", { auth: true });
  return res.entries.map((e) => ({
    workId: e.workId,
    purchasedAt: formatDate(e.purchasedAt),
    progress: e.progress,
    status: e.status,
    isOwn: e.isOwn,
    work: e.work ? normalizeWork(e.work) : null,
  }));
}

export async function fetchMyNotifications(): Promise<NotificationItem[]> {
  const res = await apiFetch<{
    notifications: Array<{
      id: string;
      type: NotificationItem["type"];
      text: string;
      workId: string | null;
      at: string;
      createdAt: number;
    }>;
  }>("/api/portal/me/notifications", { auth: true });
  return res.notifications.map((n) => ({
    id: n.id,
    type: n.type,
    text: n.text,
    at: n.at,
    workId: n.workId ?? undefined,
  }));
}

export async function fetchMyWorks(): Promise<Work[]> {
  const res = await apiFetch<{ works: ApiWork[] }>("/api/portal/me/works", {
    auth: true,
  });
  return res.works.map(normalizeWork);
}

// =============================================================
// 書き込み系: ライブラリ / レビュー / 公開
// =============================================================

export async function addToLibrary(workId: string): Promise<{
  ok: true;
  pricePaid: number;
  isOwn: boolean;
}> {
  return apiFetch(`/api/portal/me/library/${encodeURIComponent(workId)}`, {
    method: "POST",
    auth: true,
  });
}

export async function removeFromLibrary(workId: string): Promise<{ ok: true }> {
  return apiFetch(`/api/portal/me/library/${encodeURIComponent(workId)}`, {
    method: "DELETE",
    auth: true,
  });
}

export interface MyWorkStatus {
  inLibrary: boolean;
  isOwn: boolean;
  status: string | null;
  progress: number;
  myReview: { id: string; rating: number; body: string } | null;
}

export async function fetchMyWorkStatus(workId: string): Promise<MyWorkStatus> {
  return apiFetch<MyWorkStatus>(
    `/api/portal/works/${encodeURIComponent(workId)}/my-status`,
    { auth: true },
  );
}

export async function postReview(
  workId: string,
  rating: number,
  body: string,
): Promise<{ ok: true }> {
  return apiFetch(`/api/portal/works/${encodeURIComponent(workId)}/reviews`, {
    method: "POST",
    auth: true,
    body: { rating, body },
  });
}

export interface PublishInput {
  projectId: string;
  title: string;
  shortDescription?: string;
  description?: string;
  genre?: string;
  ageRating?: string;
  price?: number;
  estimatedPlayTime?: number;
  tags?: string[];
  isPublished?: boolean;
}

export async function publishWork(input: PublishInput): Promise<{ work: Work | null }> {
  const res = await apiFetch<{ work: ApiWork | null }>("/api/portal/works", {
    method: "POST",
    auth: true,
    body: input,
  });
  return { work: res.work ? normalizeWork(res.work) : null };
}

export async function unpublishWork(workId: string): Promise<{ ok: true }> {
  return apiFetch(`/api/portal/works/${encodeURIComponent(workId)}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function fetchMySales(): Promise<MonthlySales[]> {
  const res = await apiFetch<{ months: MonthlySales[] }>("/api/portal/me/sales", {
    auth: true,
  });
  return res.months.map((m) => ({ m: m.m, revenue: m.revenue }));
}

// =============================================================
// 補助
// =============================================================

function formatDate(ms: number | null | undefined): string {
  if (!ms) return "";
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatDateString(ms: number | string | null | undefined): string {
  if (ms == null) return "";
  if (typeof ms === "string") return ms;
  return formatDate(ms);
}
